import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useStrategyAnalytics } from '@/hooks/useStrategyAnalytics';
import { 
  TrendingUp, 
  Users, 
  Target, 
  Activity, 
  ChevronLeft, 
  ChevronRight,
  Play,
  BarChart3,
  TestTube,
  Star
} from 'lucide-react';

interface DisplayStrategy {
  id: string;
  name: string;
  description: string;
  winRate: number;
  netProfit: number | null;
  activeUsers: number;
  riskLevel: 'low' | 'medium' | 'high';
  totalTrades: number;
  hasSufficientData: boolean;
}

const defaultStrategies: DisplayStrategy[] = [
  {
    id: 'default-1',
    name: 'RSI Reversal Bot',
    description: 'Buy when RSI drops below 30 and MACD turns positive. Perfect for catching oversold bounces.',
    winRate: 84,
    netProfit: null,
    activeUsers: 47,
    riskLevel: 'medium',
    totalTrades: 0,
    hasSufficientData: false,
  },
  {
    id: 'default-2',
    name: 'Momentum Breakout',
    description: 'Captures strong price movements when volume spikes above average and price breaks resistance.',
    winRate: 76,
    netProfit: null,
    activeUsers: 32,
    riskLevel: 'high',
    totalTrades: 0,
    hasSufficientData: false,
  },
  {
    id: 'default-3',
    name: 'EMA Crossover Scalper',
    description: 'Quick scalping strategy using EMA crossovers with tight stop losses.',
    winRate: 68,
    netProfit: null,
    activeUsers: 89,
    riskLevel: 'low',
    totalTrades: 0,
    hasSufficientData: false,
  },
];

const StrategyOfTheDay: React.FC = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const { analytics, strategyOfTheDay } = useStrategyAnalytics();

  // Build display list: merge real analytics into defaults
  const displayStrategies: DisplayStrategy[] = defaultStrategies.map((def) => {
    const match = analytics.find(a =>
      a.strategy_name.toLowerCase().includes(def.name.toLowerCase().split(' ')[0])
    );
    if (match && match.total_trades >= 5) {
      return {
        ...def,
        winRate: match.win_rate,
        netProfit: match.net_profit,
        activeUsers: match.active_users,
        totalTrades: match.total_trades,
        hasSufficientData: true,
      };
    }
    return def;
  });

  const currentStrategy = displayStrategies[currentIndex] || displayStrategies[0];

  const nextStrategy = () => setCurrentIndex((prev) => (prev + 1) % displayStrategies.length);
  const prevStrategy = () => setCurrentIndex((prev) => (prev - 1 + displayStrategies.length) % displayStrategies.length);

  const getRiskBadge = (risk: string) => {
    switch (risk) {
      case 'low': return <Badge variant="outline" className="text-green-500 border-green-500/30 text-[10px]">Low Risk</Badge>;
      case 'high': return <Badge variant="outline" className="text-destructive border-destructive/30 text-[10px]">High Risk</Badge>;
      default: return <Badge variant="outline" className="text-amber-500 border-amber-500/30 text-[10px]">Medium Risk</Badge>;
    }
  };

  return (
    <Card className="bg-card/40 border-border/20 rounded-xl">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Star className="h-4 w-4 text-amber-500" />
            <CardTitle className="text-base">Strategy of the Day</CardTitle>
          </div>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={prevStrategy}>
              <ChevronLeft className="h-3.5 w-3.5" />
            </Button>
            <span className="text-xs text-muted-foreground tabular-nums">
              {currentIndex + 1}/{displayStrategies.length}
            </span>
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={nextStrategy}>
              <ChevronRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="pt-0">
        <motion.div
          key={currentIndex}
          initial={{ opacity: 0, x: 30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.2 }}
          className="space-y-3"
        >
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className="text-sm font-semibold">{currentStrategy.name}</h3>
              <p className="text-xs text-muted-foreground mt-0.5">{currentStrategy.description}</p>
            </div>
            {getRiskBadge(currentStrategy.riskLevel)}
          </div>

          <div className="flex gap-4">
            <div className="text-center">
              <div className="text-sm font-bold text-green-500">{currentStrategy.winRate.toFixed(1)}%</div>
              <p className="text-[10px] text-muted-foreground">Win Rate</p>
            </div>

            {currentStrategy.hasSufficientData && currentStrategy.netProfit !== null && (
              <div className="text-center">
                <div className={`text-sm font-bold ${currentStrategy.netProfit >= 0 ? 'text-green-500' : 'text-destructive'}`}>
                  {currentStrategy.netProfit >= 0 ? '+' : ''}${currentStrategy.netProfit.toFixed(2)}
                </div>
                <p className="text-[10px] text-muted-foreground">Net P&L</p>
              </div>
            )}

            <div className="text-center">
              <div className="text-sm font-bold">{currentStrategy.activeUsers}</div>
              <p className="text-[10px] text-muted-foreground">Active Users</p>
            </div>

            {currentStrategy.totalTrades > 0 && (
              <div className="text-center">
                <div className="text-sm font-bold">{currentStrategy.totalTrades}</div>
                <p className="text-[10px] text-muted-foreground">Trades</p>
              </div>
            )}
          </div>
        </motion.div>
      </CardContent>
    </Card>
  );
};

export default StrategyOfTheDay;
